import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation } from "@apollo/client/react";

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ScioMark } from "@/components/ui/scio-mark";
import { PublicHeader } from "@/components/layout/public-header";
import { LegalFooter } from "@/components/legal/legal-footer";
import { RESET_PASSWORD } from "@/graphql/mutations/account";

/** Mirrors Student::MIN_PASSWORD_LENGTH, which is what actually decides. */
const MIN_PASSWORD = 8;

type ResetResult = { resetPassword: { success: boolean; error: string | null } };

/**
 * Setting a new password from a link in an email.
 *
 * A form rather than a bare link, and that is the one place this differs from
 * `/confirm/:token` — which is a Rails redirect precisely because it has nothing
 * to ask. Mail clients prefetch links, and a reset that happened on prefetch
 * would spend the token before its owner ever pressed anything.
 *
 * Reachable signed out, outside the sidebar: somebody who cannot get into their
 * account is exactly who this is for.
 */
export default function ResetPasswordPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const [password, setPassword] = useState("");
  const [repeat, setRepeat] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const [resetPassword, { loading }] = useMutation<ResetResult>(RESET_PASSWORD, {
    onCompleted: (data) => {
      if (data.resetPassword.success) setDone(true);
      else setError(data.resetPassword.error || "Nepavyko nustatyti slaptažodžio.");
    },
    onError: () => setError("Nepavyko susisiekti su serveriu. Bandykite dar kartą."),
  });

  // Checked here and nowhere on the server: a repeated password is a guard
  // against a typo, not a rule about the account. The server has no second field
  // to compare and does not need one.
  const mismatch = repeat.length > 0 && password !== repeat;
  const ready = password.length >= MIN_PASSWORD && password === repeat;

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    resetPassword({ variables: { token, password } });
  };

  return (
    <div className="min-h-screen bg-background">
      <PublicHeader action="login" />

      <div className="flex items-center justify-center px-4 pb-16 pt-4 sm:pt-10">
        <Card className="w-full max-w-md">
          <CardHeader className="space-y-1">
            <ScioMark size={44} className="mx-auto mb-2 text-primary" title="Scio" />
            <CardTitle className="text-center text-2xl font-bold tracking-tight">
              {done ? "Slaptažodis pakeistas" : "Naujas slaptažodis"}
            </CardTitle>
            <CardDescription className="text-center">
              {done
                ? "Dabar galite prisijungti su nauju slaptažodžiu."
                : "Įrašykite naują slaptažodį du kartus, kad nebūtų klaidos."}
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-4">
            {done ? (
              /* Not signed in automatically: a link out of a mailbox is a weaker
                 thing to hold than a typed password, and they have just chosen
                 one. Straight to the login, where it is used. */
              <Button className="w-full" onClick={() => navigate("/auth/login")}>
                Prisijungti
              </Button>
            ) : (
              <form onSubmit={onSubmit} className="space-y-4">
                {error && (
                  <Alert variant="destructive">
                    <AlertDescription>
                      {error}
                      {/* A dead link is not something they can fix by retyping,
                          so the way out is named in the same breath. */}
                      <Link
                        to="/auth/forgot"
                        className="mt-2 block underline underline-offset-4"
                      >
                        Paprašyti naujos nuorodos
                      </Link>
                    </AlertDescription>
                  </Alert>
                )}

                <div className="space-y-2">
                  <Label htmlFor="password">Naujas slaptažodis</Label>
                  <Input
                    id="password"
                    type="password"
                    autoComplete="new-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={loading}
                    minLength={MIN_PASSWORD}
                    autoFocus
                    required
                  />
                  <p className="text-xs text-muted-foreground">Bent {MIN_PASSWORD} simboliai.</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="repeat">Pakartokite slaptažodį</Label>
                  <Input
                    id="repeat"
                    type="password"
                    autoComplete="new-password"
                    value={repeat}
                    onChange={(e) => setRepeat(e.target.value)}
                    disabled={loading}
                    aria-invalid={mismatch ? true : undefined}
                    required
                  />
                  {mismatch && (
                    <p className="text-xs text-destructive">Slaptažodžiai nesutampa.</p>
                  )}
                </div>

                <Button type="submit" className="w-full" disabled={loading || !ready}>
                  {loading ? "Saugoma..." : "Nustatyti slaptažodį"}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
      <LegalFooter />
    </div>
  );
}
